import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Link, NavLink, useLocation } from 'react-router-dom';
import {
  Search,
  ShieldCheck,
  FileText,
  Sparkles,
  ArrowRight,
  Mail,
  Menu,
  X,
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Design tokens — placeholder TruSleuths palette. Refine during the
// look-and-feel pass (kept in sync with tailwind.config.js `sleuth*` tokens).
// ---------------------------------------------------------------------------
const colors = {
  indigo: '#3730A3',
  violet: '#6D28D9',
  night: '#0F172A',
  slate: '#475569',
  mist: '#F1F5F9',
  cloud: '#F9FAFB',
  amber: '#F59E0B',
};

// Studio / company details — update when finalized.
const site = {
  appName: 'TruSleuths',
  company: 'TruSleuths',
  contactEmail: 'support@trusleuths.com',
  // Last-updated date shown on the legal pages.
  lastUpdated: 'October 23, 2026',
};

const navLinks = [
  { name: 'Home', path: '/' },
  { name: 'Terms', path: '/terms' },
  { name: 'Privacy', path: '/privacy' },
];

// ---------------------------------------------------------------------------
// App shell
// ---------------------------------------------------------------------------
const App = () => {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
};

const AppContent = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
    setIsMenuOpen(false);
  }, [location.pathname]);

  return (
    <div
      className="min-h-screen flex flex-col font-sans text-slate-800 bg-[#F9FAFB]"
      style={{ fontFamily: "'Montserrat', sans-serif" }}
    >
      <Navbar isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} />

      <main className="flex-1">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="*" element={<HomePage />} />
        </Routes>
      </main>

      <Footer />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Navigation
// ---------------------------------------------------------------------------
const Navbar = ({ isMenuOpen, setIsMenuOpen }) => {
  return (
    <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-20 items-center">
          {/* Logo */}
          <Link to="/" className="flex items-center cursor-pointer group" aria-label="TruSleuths home">
            <span
              className="flex items-center justify-center h-10 w-10 rounded-xl mr-3 text-white group-hover:scale-105 transition-transform"
              style={{ backgroundColor: colors.indigo }}
            >
              <Search size={22} aria-hidden="true" />
            </span>
            <span
              className="text-2xl font-bold tracking-tight text-slate-900"
              style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
            >
              Tru<span style={{ color: colors.violet }}>Sleuths</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <NavLink
                key={link.path}
                to={link.path}
                className={({ isActive }) =>
                  `text-sm font-semibold transition-colors hover:text-[#6D28D9] ${
                    isActive ? 'text-[#6D28D9]' : 'text-slate-600'
                  }`
                }
              >
                {link.name}
              </NavLink>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-slate-600 p-2"
              aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
            >
              {isMenuOpen ? <X size={28} aria-hidden="true" /> : <Menu size={28} aria-hidden="true" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Nav Overlay */}
      {isMenuOpen && (
        <div className="md:hidden bg-white border-b border-slate-100 absolute w-full left-0 p-4 space-y-4 shadow-xl">
          {navLinks.map((link) => (
            <NavLink
              key={link.path}
              to={link.path}
              className={({ isActive }) =>
                `block text-base font-semibold transition-colors hover:text-[#6D28D9] ${
                  isActive ? 'text-[#6D28D9]' : 'text-slate-700'
                }`
              }
            >
              {link.name}
            </NavLink>
          ))}
        </div>
      )}
    </nav>
  );
};

// ---------------------------------------------------------------------------
// Footer
// ---------------------------------------------------------------------------
const Footer = () => {
  const year = new Date().getFullYear();
  return (
    <footer className="bg-[#0F172A] text-slate-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-8">
          <div className="max-w-sm">
            <div className="flex items-center mb-4">
              <span
                className="flex items-center justify-center h-9 w-9 rounded-xl mr-3 text-white"
                style={{ backgroundColor: colors.violet }}
              >
                <Search size={20} aria-hidden="true" />
              </span>
              <span
                className="text-xl font-bold text-white"
                style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
              >
                {site.appName}
              </span>
            </div>
            <p className="text-sm text-slate-400">
              An immersive mixed-reality detective experience for Meta Horizon.
            </p>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-white uppercase tracking-wider mb-4">Legal</h3>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to="/terms" className="hover:text-white transition-colors">
                  Terms &amp; Conditions
                </Link>
              </li>
              <li>
                <Link to="/privacy" className="hover:text-white transition-colors">
                  Privacy Policy
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-white uppercase tracking-wider mb-4">Contact</h3>
            <a
              href={`mailto:${site.contactEmail}`}
              className="flex items-center text-sm hover:text-white transition-colors"
            >
              <Mail size={16} className="mr-2" aria-hidden="true" />
              {site.contactEmail}
            </a>
          </div>
        </div>

        <div className="border-t border-slate-700 mt-10 pt-6 text-sm text-slate-400">
          &copy; {year} {site.company}. All rights reserved.
        </div>
      </div>
    </footer>
  );
};

// ---------------------------------------------------------------------------
// Shared layout helpers
// ---------------------------------------------------------------------------
const LegalLayout = ({ icon: Icon, title, children }) => (
  <div className="animate-in">
    <div className="bg-gradient-to-br from-[#3730A3] to-[#6D28D9] text-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="flex items-center mb-4">
          <Icon size={28} className="mr-3" aria-hidden="true" />
          <h1 className="text-3xl md:text-4xl font-bold" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            {title}
          </h1>
        </div>
        <p className="text-sm text-indigo-100">Last updated: {site.lastUpdated}</p>
      </div>
    </div>
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="prose prose-slate max-w-none space-y-8">{children}</div>
    </div>
  </div>
);

const LegalSection = ({ heading, children }) => (
  <section>
    <h2
      className="text-xl font-bold text-slate-900 mb-3"
      style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
    >
      {heading}
    </h2>
    <div className="space-y-3 text-slate-600 leading-relaxed">{children}</div>
  </section>
);

// ---------------------------------------------------------------------------
// Home page
// ---------------------------------------------------------------------------
const HomePage = () => {
  return (
    <div className="animate-in">
      {/* Hero */}
      <section className="bg-gradient-to-br from-[#0F172A] via-[#3730A3] to-[#6D28D9] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32 text-center">
          <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-white/10 text-sm font-semibold mb-6">
            <Sparkles size={16} className="mr-2" aria-hidden="true" />
            Mixed Reality for Meta Horizon
          </span>
          <h1
            className="text-4xl md:text-6xl font-extrabold tracking-tight mb-6"
            style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
          >
            Tru<span className="text-amber-400">Sleuths</span>
          </h1>
          <p className="max-w-2xl mx-auto text-lg md:text-xl text-indigo-100 mb-10">
            Step into an immersive detective experience. Investigate scenes, follow the clues,
            and crack the case — right in your living room.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/terms"
              className="inline-flex items-center justify-center px-7 py-3 rounded-full font-bold text-white transition-all hover:opacity-90 active:scale-95"
              style={{ backgroundColor: colors.amber }}
            >
              Terms &amp; Conditions
              <ArrowRight size={18} className="ml-2" aria-hidden="true" />
            </Link>
            <Link
              to="/privacy"
              className="inline-flex items-center justify-center px-7 py-3 rounded-full font-bold text-white bg-white/10 hover:bg-white/20 transition-all active:scale-95"
            >
              Privacy Policy
            </Link>
          </div>
        </div>
      </section>

      {/* Feature cards */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid gap-8 md:grid-cols-3">
          {[
            {
              icon: Search,
              title: 'Investigate',
              body: 'Examine mixed-reality crime scenes layered onto your real space and gather evidence.',
            },
            {
              icon: Sparkles,
              title: 'Deduce',
              body: 'Piece together clues with an AI-driven case engine that adapts to your choices.',
            },
            {
              icon: ShieldCheck,
              title: 'Play Safely',
              body: 'Designed with player privacy and comfort in mind from the ground up.',
            },
          ].map((f) => (
            <div
              key={f.title}
              className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100 hover:shadow-md transition-shadow"
            >
              <span
                className="flex items-center justify-center h-12 w-12 rounded-xl mb-5 text-white"
                style={{ backgroundColor: colors.indigo }}
              >
                <f.icon size={24} aria-hidden="true" />
              </span>
              <h3
                className="text-lg font-bold text-slate-900 mb-2"
                style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
              >
                {f.title}
              </h3>
              <p className="text-slate-600 leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Legal links strip */}
      <section className="bg-[#F1F5F9]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <h2
            className="text-2xl font-bold text-slate-900 mb-4"
            style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
          >
            Policies &amp; Legal
          </h2>
          <p className="text-slate-600 max-w-xl mx-auto mb-8">
            Review how TruSleuths handles your data and the terms that govern your use of the experience.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/terms"
              className="inline-flex items-center justify-center px-6 py-3 rounded-full font-semibold text-white transition-all hover:opacity-90"
              style={{ backgroundColor: colors.indigo }}
            >
              <FileText size={18} className="mr-2" aria-hidden="true" />
              Terms &amp; Conditions
            </Link>
            <Link
              to="/privacy"
              className="inline-flex items-center justify-center px-6 py-3 rounded-full font-semibold text-white transition-all hover:opacity-90"
              style={{ backgroundColor: colors.violet }}
            >
              <ShieldCheck size={18} className="mr-2" aria-hidden="true" />
              Privacy Policy
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Terms page  (placeholder copy — replace with reviewed legal text)
// ---------------------------------------------------------------------------
const TermsPage = () => {
  return (
    <LegalLayout icon={FileText} title="Terms &amp; Conditions">
      <p className="text-slate-600">
        These Terms &amp; Conditions (&ldquo;Terms&rdquo;) govern your access to and use of the{' '}
        {site.appName} application and related services (the &ldquo;Experience&rdquo;). By downloading,
        installing, or using the Experience, you agree to these Terms.
      </p>

      <LegalSection heading="1. Acceptance of Terms">
        <p>
          By accessing or using {site.appName}, you confirm that you can form a binding contract and
          that you accept these Terms. If you do not agree, do not use the Experience.
        </p>
      </LegalSection>

      <LegalSection heading="2. Eligibility">
        <p>
          You must meet the minimum age and account requirements of the Meta Horizon Store and your
          local jurisdiction to use {site.appName}.
        </p>
      </LegalSection>

      <LegalSection heading="3. License">
        <p>
          Subject to these Terms, {site.company} grants you a limited, non-exclusive, non-transferable,
          revocable license to use the Experience for personal, non-commercial entertainment purposes.
        </p>
      </LegalSection>

      <LegalSection heading="4. Acceptable Use">
        <p>
          You agree not to reverse engineer, modify, resell, or misuse the Experience, or use it in any
          way that violates applicable law or the Meta Horizon Store policies.
        </p>
      </LegalSection>

      <LegalSection heading="5. Intellectual Property">
        <p>
          All content, trademarks, and software associated with {site.appName} are owned by{' '}
          {site.company} or its licensors and are protected by applicable intellectual property laws.
        </p>
      </LegalSection>

      <LegalSection heading="6. Disclaimers">
        <p>
          The Experience is provided &ldquo;as is&rdquo; without warranties of any kind. {site.company}{' '}
          does not warrant that the Experience will be uninterrupted or error-free.
        </p>
      </LegalSection>

      <LegalSection heading="7. Limitation of Liability">
        <p>
          To the maximum extent permitted by law, {site.company} shall not be liable for any indirect,
          incidental, or consequential damages arising from your use of the Experience.
        </p>
      </LegalSection>

      <LegalSection heading="8. Changes to These Terms">
        <p>
          We may update these Terms from time to time. Continued use of the Experience after changes
          take effect constitutes acceptance of the revised Terms.
        </p>
      </LegalSection>

      <LegalSection heading="9. Contact">
        <p>
          Questions about these Terms? Contact us at{' '}
          <a href={`mailto:${site.contactEmail}`} className="text-[#6D28D9] font-semibold">
            {site.contactEmail}
          </a>
          .
        </p>
      </LegalSection>
    </LegalLayout>
  );
};

// ---------------------------------------------------------------------------
// Privacy page  (placeholder copy — replace with reviewed legal text)
// ---------------------------------------------------------------------------
const PrivacyPage = () => {
  return (
    <LegalLayout icon={ShieldCheck} title="Privacy Policy">
      <p className="text-slate-600">
        This Privacy Policy explains how {site.company} (&ldquo;we&rdquo;, &ldquo;us&rdquo;) collects,
        uses, and protects information when you use the {site.appName} Experience.
      </p>

      <LegalSection heading="1. Information We Collect">
        <p>
          We may collect limited information needed to operate the Experience, such as device and
          gameplay data. We do not sell your personal information.
        </p>
      </LegalSection>

      <LegalSection heading="2. How We Use Information">
        <p>
          We use collected information to provide, maintain, and improve the Experience, and to
          troubleshoot issues and ensure a safe, comfortable experience.
        </p>
      </LegalSection>

      <LegalSection heading="3. Mixed Reality &amp; Sensor Data">
        <p>
          {site.appName} may use spatial and sensor data provided by your headset to render the
          mixed-reality experience. This data is processed on-device where possible and is not used to
          identify you.
        </p>
      </LegalSection>

      <LegalSection heading="4. Data Sharing">
        <p>
          We share information only with service providers who help us operate the Experience, or when
          required by law. These providers are bound by confidentiality obligations.
        </p>
      </LegalSection>

      <LegalSection heading="5. Data Retention">
        <p>
          We retain information only for as long as necessary to provide the Experience and to meet
          legal and operational requirements.
        </p>
      </LegalSection>

      <LegalSection heading="6. Children's Privacy">
        <p>
          {site.appName} is not directed to children under the age required by the Meta Horizon Store
          and applicable law. We do not knowingly collect data from such children.
        </p>
      </LegalSection>

      <LegalSection heading="7. Your Rights">
        <p>
          Depending on your jurisdiction, you may have rights to access, correct, or delete your
          personal information. Contact us to exercise these rights.
        </p>
      </LegalSection>

      <LegalSection heading="8. Changes to This Policy">
        <p>
          We may update this Privacy Policy from time to time. We will revise the &ldquo;Last
          updated&rdquo; date above when we do.
        </p>
      </LegalSection>

      <LegalSection heading="9. Contact">
        <p>
          Questions about this Privacy Policy? Contact us at{' '}
          <a href={`mailto:${site.contactEmail}`} className="text-[#6D28D9] font-semibold">
            {site.contactEmail}
          </a>
          .
        </p>
      </LegalSection>
    </LegalLayout>
  );
};

export default App;
